import { Player, Theme } from "../api/types";
import { normalizeHexForSvg } from "./color";

const S = 1.3; // size multiplier

const OVAL_RX = 18 * S;
const OVAL_RY = 12 * S;
const MODERN_OVAL_SCALE = 0.87;
// Classic theme renders receiverOval/linemanOval as true circles, not ovals
const CLASSIC_CIRCLE_R = Math.min(OVAL_RX, OVAL_RY);
const SQUARE_HALF = (28 * S) / 2;
const DIAMOND_HALF = (28 * S * 1.2) / 2;
const LABEL_FONT_SIZE = 13 * S;
const STROKE_WIDTH = 3;

// Callers must pass plays through normalizePlay() before buildShapesLayer().
function normalizePlayerColor(player: Player): string {
  return normalizeHexForSvg(player.color);
}

function hexLuminance(hex: string): number {
  const normalized = normalizeHexForSvg(hex);
  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;
  // sRGB linearization
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function textColor(bgHex: string): string {
  return hexLuminance(bgHex) > 0.3 ? "#000000" : "#ffffff";
}

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function labelSvg(x: number, y: number, text: string, fill: string): string {
  return `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="central"
    font-family="Inter, sans-serif" font-size="${LABEL_FONT_SIZE}" font-weight="bold" fill="${fill}">${escapeXml(text)}</text>`;
}

// Returns { defs, element } so caller can collect all defs into one <defs> block
interface ShapeSvg {
  defs: string;
  element: string;
}

function gradientId(playId: string, playerId: string): string {
  return `grad_${playId}_${playerId}`;
}

function buildGradientDef(id: string, color: string, stateIndex: 0 | 1 | 2 | 3 | number): string {
  if (stateIndex === 0 || stateIndex === 3) return "";

  const normalizedColor = normalizeHexForSvg(color);
  const [colorStop, blackStop] =
    stateIndex === 1
      ? [`<stop offset="49%" stop-color="${normalizedColor}"/>`, `<stop offset="51%" stop-color="#000000"/>`]
      : [`<stop offset="49%" stop-color="#000000"/>`, `<stop offset="51%" stop-color="${normalizedColor}"/>`];

  return `<linearGradient id="${id}" x1="0" x2="1" y1="0" y2="0">
    ${colorStop}${blackStop}
  </linearGradient>`;
}

function shapeFill(playId: string, playerId: string, color: string, stateIndex: 0 | 1 | 2 | 3 | number): string {
  if (stateIndex === 0 || stateIndex === 3) return color;
  return `url(#${gradientId(playId, playerId)})`;
}

function receiverOvalRadii(modern: boolean): [number, number] {
  const rx = modern ? OVAL_RX * MODERN_OVAL_SCALE : CLASSIC_CIRCLE_R;
  const ry = modern ? OVAL_RY * MODERN_OVAL_SCALE : CLASSIC_CIRCLE_R;
  return [rx, ry];
}

export function buildShapesLayer(play: { _id: string; shape_list: Player[]; theme: Theme }): ShapeSvg {
  const defs: string[] = [];
  const elements: string[] = [];
  const modern = play.theme === "modern";

  for (const player of play.shape_list) {
    const { id, position: { x, y }, text } = player;
    const { shapeType, stateIndex } = player;
    const color = normalizePlayerColor(player);
    const gid = gradientId(play._id, id);

    let defStr = "";
    let elemStr = "";
    const tColor = textColor(color);

    switch (shapeType) {
      case "receiverOval": {
        // iOS ReceiverOvalView ignores stateIndex — always solid fill, no gradient cycling
        const [rx, ry] = receiverOvalRadii(modern);
        elemStr = `<ellipse cx="${x}" cy="${y}" rx="${rx}" ry="${ry}" fill="${color}" stroke="#000" stroke-width="${STROKE_WIDTH}"/>
${labelSvg(x, y, text, tColor)}`;
        break;
      }

      case "linemanOval": {
        const rx = modern ? OVAL_RX : CLASSIC_CIRCLE_R;
        const ry = modern ? OVAL_RY : CLASSIC_CIRCLE_R;
        defStr = buildGradientDef(gid, color, stateIndex);
        const fill = shapeFill(play._id, id, color, stateIndex);
        const extraLine =
          stateIndex === 3
            ? `<line x1="${x}" y1="${y - ry}" x2="${x}" y2="${y + ry}" stroke="#000" stroke-width="3.5"/>`
            : "";
        elemStr = `<ellipse cx="${x}" cy="${y}" rx="${rx}" ry="${ry}" fill="${fill}" stroke="#000" stroke-width="${STROKE_WIDTH}"/>
${extraLine}
${labelSvg(x, y, text, tColor)}`;
        break;
      }

      case "centerSquare": {
        defStr = buildGradientDef(gid, color, stateIndex);
        const fill = shapeFill(play._id, id, color, stateIndex);
        const sz = SQUARE_HALF * 2;
        const extraLine =
          stateIndex === 3
            ? `<line x1="${x}" y1="${y - SQUARE_HALF}" x2="${x}" y2="${y + SQUARE_HALF}" stroke="#000" stroke-width="3.5"/>`
            : "";
        elemStr = `<rect x="${x - SQUARE_HALF}" y="${y - SQUARE_HALF}" width="${sz}" height="${sz}" fill="${fill}" stroke="#000" stroke-width="${STROKE_WIDTH}"/>
${extraLine}
${labelSvg(x, y, text, tColor)}`;
        break;
      }

      case "defenderDiamond": {
        const d = DIAMOND_HALF;
        const pts = `${x},${y - d} ${x + d},${y} ${x},${y + d} ${x - d},${y}`;
        // Classic and modern: filled diamond, black outline, contrast-aware label (matches CHLK web-ui).
        defStr = buildGradientDef(gid, color, stateIndex);
        const fill = shapeFill(play._id, id, color, stateIndex);
        elemStr = `<polygon points="${pts}" fill="${fill}" stroke="#000" stroke-width="${STROKE_WIDTH}"/>
${labelSvg(x, y, text, tColor)}`;
        break;
      }

      default: {
        // Unknown/missing shapeType — render a visible fallback circle so labels aren't orphaned
        const [rx, ry] = receiverOvalRadii(modern);
        elemStr = `<ellipse cx="${x}" cy="${y}" rx="${rx}" ry="${ry}" fill="${color}" stroke="#000" stroke-width="${STROKE_WIDTH}"/>
${labelSvg(x, y, text, tColor)}`;
        break;
      }
    }

    if (defStr) defs.push(defStr);
    elements.push(elemStr);
  }

  return { defs: defs.join("\n"), element: elements.join("\n") };
}

// Exported for use in lines.ts (shape-edge offset calculation)
export { OVAL_RX, OVAL_RY, SQUARE_HALF, DIAMOND_HALF, MODERN_OVAL_SCALE, CLASSIC_CIRCLE_R };
export type { ShapeSvg };
